/**
 * Panel de permisos config-driven (modelo Partnerships → Talent Acquisition →
 * Learning): admins de la sección (base fijos, con candado) + viewers por
 * persona (picker del community map) + viewers por team/área (en vivo). Cada
 * sección le pasa sus hooks y su namespace de i18n; `children` va debajo del
 * panel (cards extra, p. ej. las reglas de cálculo de TA).
 */

import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Plus, Shield, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { RosterTable, type RosterMember } from '@/components/governance/RosterTable';

type PickerMode = 'admins' | 'viewers';

export interface GovernanceView {
  admins: string[];
  baseAdmins: string[];
  viewers: string[];
  viewerTeams: string[];
}
export interface GovernancePayload {
  admins: string[];
  viewers: string[];
  viewerTeams: string[];
}
/** Persona del community map tal como la muestra la tabla del roster (RosterTable). */
export type GovernanceMember = RosterMember;

export function GovernancePanel({ namespace, governance, isLoading, isError, members = [], membersLoading = false, onSave, saving, children }: {
  /** Namespace con title, subtitle, save, saving, no_access, admins_*, viewers_*, teams_*, col_*, remove, fixed_admin, not_in_map, picker_*, no_results, footer_note. */
  namespace: string;
  governance: GovernanceView | undefined;
  isLoading: boolean;
  isError: boolean;
  members?: GovernanceMember[];
  membersLoading?: boolean;
  onSave: (payload: GovernancePayload, onSuccess: () => void) => void;
  saving: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const k = namespace;

  const [admins, setAdmins] = useState<string[]>([]);
  const [viewers, setViewers] = useState<string[]>([]);
  const [viewerTeams, setViewerTeams] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [picker, setPicker] = useState<PickerMode | null>(null);
  const [addTeam, setAddTeam] = useState('');

  useEffect(() => {
    if (governance) {
      setAdmins(governance.admins.map((e) => e.toLowerCase()));
      setViewers(governance.viewers.map((e) => e.toLowerCase()));
      setViewerTeams(governance.viewerTeams);
      setDirty(false);
    }
  }, [governance]);

  const baseAdmins = governance?.baseAdmins ?? [];
  const availableTeams = useMemo(() => {
    const set = new Set<string>();
    for (const m of members) { if (m.team) set.add(m.team); if (m.area) set.add(m.area); }
    for (const x of viewerTeams) set.add(x);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [members, viewerTeams]);

  const taken = useMemo(() => new Set([...admins, ...viewers]), [admins, viewers]);

  const add = (email: string) => {
    const e = email.toLowerCase();
    if (picker === 'admins') setAdmins((p) => [...p, e]);
    else setViewers((p) => [...p, e]);
    setDirty(true);
  };
  const removeAdmin = (email: string) => { if (baseAdmins.includes(email)) return; setAdmins((p) => p.filter((x) => x !== email)); setDirty(true); };
  const removeViewer = (email: string) => { setViewers((p) => p.filter((x) => x !== email)); setDirty(true); };
  const addViewerTeam = (team: string) => { if (!team || viewerTeams.includes(team)) return; setViewerTeams((p) => [...p, team]); setAddTeam(''); setDirty(true); };
  const removeViewerTeam = (team: string) => { setViewerTeams((p) => p.filter((x) => x !== team)); setDirty(true); };
  const handleSave = () => onSave({ admins, viewers, viewerTeams }, () => setDirty(false));

  if (isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  if (isError || !governance) {
    return (
      <div className="py-16 text-center space-y-3">
        <Shield className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground">{t(`${k}.no_access`)}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-brand-green" />
          <div>
            <h2 className="text-xl font-bold tracking-tight">{t(`${k}.title`)}</h2>
            <p className="text-sm text-muted-foreground">{t(`${k}.subtitle`)}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving ? t(`${k}.saving`) : t(`${k}.save`)}
        </Button>
      </div>

      {/* Admins */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">{t(`${k}.admins_title`)}</CardTitle>
            <CardDescription>{t(`${k}.admins_desc`)}</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={() => setPicker('admins')}>
            <Plus className="h-3.5 w-3.5" /> {t(`${k}.add_admin`)}
          </Button>
        </CardHeader>
        <CardContent>
          <RosterTable emails={admins} members={members} namespace={k} locked={(e) => baseAdmins.includes(e)} empty={t(`${k}.admins_empty`)} onRemove={removeAdmin} />
        </CardContent>
      </Card>

      {/* Viewers por persona */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">{t(`${k}.viewers_title`)}</CardTitle>
            <CardDescription>{t(`${k}.viewers_desc`)}</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={() => setPicker('viewers')}>
            <Plus className="h-3.5 w-3.5" /> {t(`${k}.add_viewer`)}
          </Button>
        </CardHeader>
        <CardContent>
          <RosterTable emails={viewers} members={members} namespace={k} empty={t(`${k}.viewers_empty`)} onRemove={removeViewer} />
        </CardContent>
      </Card>

      {/* Viewers por team */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t(`${k}.teams_title`)}</CardTitle>
          <CardDescription>{t(`${k}.teams_desc`)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {viewerTeams.length === 0 && <p className="text-sm text-muted-foreground">{t(`${k}.teams_empty`)}</p>}
            {viewerTeams.map((team) => (
              <Badge key={team} variant="secondary" className="gap-1.5 py-1 pl-2.5 pr-1 text-sm">
                {team}
                <button onClick={() => removeViewerTeam(team)} className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10" aria-label={t(`${k}.remove`)}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex max-w-sm items-center gap-2">
            <Select value={addTeam} onValueChange={(v) => addViewerTeam(v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={membersLoading ? t(`${k}.loading_teams`) : t(`${k}.add_team`)} />
              </SelectTrigger>
              <SelectContent>
                {availableTeams.filter((x) => !viewerTeams.includes(x)).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t(`${k}.footer_note`)}
      </p>

      {children}

      <MemberPickerDialog
        open={picker !== null}
        onOpenChange={(o) => !o && setPicker(null)}
        title={picker === 'admins' ? t(`${k}.add_admin`) : t(`${k}.add_viewer`)}
        description={t(`${k}.picker_desc`)}
        searchPlaceholder={t(`${k}.picker_search`)}
        noResultsText={t(`${k}.no_results`)}
        members={members}
        loading={membersLoading}
        exclude={taken}
        onPick={(m) => add(m.email)}
      />
    </div>
  );
}
